import type { Row } from "@tanstack/react-table";
import { Children, isValidElement, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { Transaction } from "@/apis/gql/types";
import { BaseTable } from "@/components/shared/data-table";
import { NoRecordsEmpty } from "@/components/shared/data-table/empty-message";
import { TableBodyRow } from "@/components/ui/virtualized-table";

import { TransactionsTableFilters } from "../transactions-table-filters";
import type { TransactionsTodosTableInstance } from "../use-transactions-todos-table";

type Props = TransactionsTodosTableInstance & {
  children?: React.ReactNode;
};

const ActionBarSlot = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

const TransactionsTodosRoot = ({
  todosLoading,
  table,
  globalSearch,
  setGlobalSearch,
  filters,
  setFilters,
  clickableColumns,
  columnVisibility,
  columns,
  data,
  stickyColumns,
  footer,
  children,
}: Props) => {
  const tableRef = useRef(null);
  const { t } = useTranslation();

  const showDoneMessage =
    Object.keys(filters).length <= 3 &&
    !(filters as any)?.bankAccountId?.length &&
    !globalSearch;

  let actionBarContent: React.ReactNode = null;

  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === ActionBarSlot) {
      actionBarContent = child.props.children;
    }
  });

  function handleRowClick(
    event: React.MouseEvent,
    row: Row<Transaction>,
    index: number,
  ) {
    table.setRowSelection((prev) => ({
      [row.id]: !prev[row.id],
    }));
  }

  return (
    <div className="h-full flex flex-row">
      <div
        className="flex flex-col gap-2 h-full flex-1 relative"
        id="transactions-table"
      >
        <p className="text-sm text-gray-600 mb-2 mt-2 font-medium">
          {t("transactions.todos_guiding_message")}
        </p>
        <TransactionsTableFilters
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          filters={filters}
          setFilters={setFilters}
        />
        <BaseTable
          table={table}
          clickableColumns={clickableColumns}
          columnVisibility={columnVisibility}
          columns={columns}
          data={data}
          stickyColumns={stickyColumns}
          footer={footer}
          loading={todosLoading}
          ref={tableRef}
          estimateSize={92}
          onRowClick={handleRowClick}
          emptyStateComponent={
            showDoneMessage ? <NoRecordsEmpty state="done" /> : 0
          }
        >
          {({
            rows,
            rowVirtualizer,
            table: tableInstance,
            clickableColumns,
            onRowClick,
            rowValidationSchema,
          }) => (
            <>
              {rowVirtualizer.getVirtualItems().map((virtualRow: any) => {
                const row = rows[virtualRow.index];
                if (!row) return null;

                const isExiting = tableInstance.isRowExiting(row.id);

                return (
                  <TableBodyRow
                    key={row.id}
                    row={row}
                    virtualRow={virtualRow}
                    rowVirtualizer={rowVirtualizer}
                    table={tableInstance}
                    clickableColumns={clickableColumns}
                    onRowClick={onRowClick}
                    rowValidationSchema={rowValidationSchema}
                    className={`overflow-hidden ${
                      isExiting
                        ? "transition-all duration-300 ease-in-out !min-h-0 !max-h-0 !opacity-0 scale-1 [&_td]:!py-0 [&_td]:!px-0 !border-0"
                        : ""
                    }`}
                  />
                );
              })}
            </>
          )}
        </BaseTable>
      </div>
      {actionBarContent}
    </div>
  );
};

export const TransactionsTodos = Object.assign(TransactionsTodosRoot, {
  ActionBar: ActionBarSlot,
});
